<script setup lang="ts">
import type { ByCityResponse } from '~~/server/api/establishments/by-city.get'

const { t, locale } = useI18n()
const localePath = useLocalePath()
const ogLocale = useOgLocale()
const config = useRuntimeConfig()
const route = useRoute()
const { regions } = useRegionsData()

definePageMeta({
  layout: 'default',
  validate: (route) => {
    const codeSlug = route.params.codeSlug as string
    const city = route.params.city as string
    return /^\d{1,3}[a-zA-Z]?-.+$/.test(codeSlug) && /^[a-z0-9-]+$/.test(city)
  },
})

const codeSlug = computed(() => route.params.codeSlug as string)
const citySlug = computed(() => route.params.city as string)

const departmentCode = computed(() => {
  const match = codeSlug.value.match(/^(\d{1,3}[A-Za-z]?)-(.+)$/)
  return match ? match[1]!.toUpperCase() : null
})
const departmentSlug = computed(() => {
  const match = codeSlug.value.match(/^(\d{1,3}[A-Za-z]?)-(.+)$/)
  return match ? match[2]! : null
})

// --- Département parent (breadcrumb / SEO / garde slug) ---
const department = computed(() => {
  const code = departmentCode.value
  if (!code) return null
  for (const region of regions) {
    const dept = region.departments.find(d => d.code === code)
    if (dept) return { ...dept, region }
  }
  return null
})

// Garde : codeSlug malformé ou slug département incohérent → 404 server-side.
if (!departmentCode.value || !departmentSlug.value) {
  throw createError({ statusCode: 404, statusMessage: 'Not Found', fatal: true })
}
if (department.value && department.value.slug !== departmentSlug.value) {
  throw createError({ statusCode: 404, statusMessage: 'Not Found', fatal: true })
}

// --- Données ville ---
const { data } = await useAsyncData<ByCityResponse>(
  () => `city-${codeSlug.value}-${citySlug.value}`,
  () => rawFetch<ByCityResponse>('/api/establishments/by-city', {
    query: { departmentCode: departmentCode.value, citySlug: citySlug.value },
  }),
)

// Doctrine : l'existence de la page est gouvernée par le listing EXACT.
// Zéro établissement exact → la ville n'a pas de page → 404.
if (!data.value || data.value.exact.length === 0) {
  throw createError({ statusCode: 404, statusMessage: 'Not Found', fatal: true })
}

const cityName = computed(() => data.value?.cityName ?? '')
const exactCount = computed(() => data.value?.exact.length ?? 0)
const departmentName = computed(() => department.value?.name ?? '')

/**
 * Suffixe de clé i18n selon le parc RÉEL de la ville (modèle `labelSuffix` du
 * fix 4.54.7 sur la page département) : `''` = chambres d'hôtes seules (rendu
 * d'avant 4.146.1), `Gite` = gîtes seuls, `All` = mixte. Une page sans chambre
 * d'hôtes ne doit jamais titrer « Chambres d'hôtes à X ».
 */
const labelSuffix = computed(() => {
  const counts = data.value?.exactCounts
  if (!counts) return ''
  if (counts.cdh > 0 && counts.gite > 0) return 'All'
  if (counts.gite > 0) return 'Gite'
  return ''
})
const k = (key: string) => `pages.ville.${key}${labelSuffix.value}`

const i18nVars = computed(() => ({
  city: cityName.value,
  department: departmentName.value,
  count: exactCount.value,
}))

// --- Robots : gating sur le count EXACT (≥ 1 → index, sinon on n'atteint
// jamais cette page car 404). EN non traduit → noindex. ---
const robotsContent = computed(() => (locale.value === 'en' ? 'noindex, follow' : 'index, follow'))

const localePrefix = computed(() => (locale.value === 'fr' ? '' : `/${locale.value}`))
const pageUrl = computed(() => `${config.public.siteUrl}${localePrefix.value}/${codeSlug.value}/ville/${citySlug.value}`)
const canonicalUrl = computed(() => `${config.public.siteUrl}/${codeSlug.value}/ville/${citySlug.value}`)

useHead(() => ({
  meta: [{ name: 'robots', content: robotsContent.value }],
  link: [{ rel: 'canonical', href: canonicalUrl.value }],
}))

useSeoMeta({
  title: () => t(k('seo.title'), i18nVars.value),
  description: () => t(k('seo.description'), i18nVars.value, exactCount.value),
  ogTitle: () => t(k('seo.title'), i18nVars.value),
  ogDescription: () => t(k('seo.description'), i18nVars.value, exactCount.value),
  ogType: 'website',
  ogSiteName: 'Holicosy',
  ogUrl: () => pageUrl.value,
  ogLocale,
})

// --- FAQ (2 questions templatées) ---
const faqItems = computed(() => [
  {
    question: t(k('faq.q1.question'), i18nVars.value),
    answer: t(k('faq.q1.answer'), i18nVars.value, exactCount.value),
  },
  {
    question: t(k('faq.q2.question'), i18nVars.value),
    answer: t('pages.ville.faq.q2.answer', i18nVars.value),
  },
])

// --- Breadcrumb : Home → Région → Département → Ville ---
const breadcrumbItems = computed(() => {
  const items: Array<{ label: string, to?: string }> = [
    { label: t('common.breadcrumb.home'), to: localePath('/') },
  ]
  if (department.value) {
    items.push({ label: department.value.region.name, to: localePath(`/regions/${department.value.region.slug}`) })
    items.push({ label: `${department.value.code} ${department.value.name}`, to: localePath(`/${codeSlug.value}`) })
  }
  items.push({ label: t('pages.ville.breadcrumb', i18nVars.value) })
  return items
})

// --- Schema.org : CollectionPage + Breadcrumb + FAQ ---
useSchemaOrg([
  defineWebPage({
    '@type': 'CollectionPage',
    'name': () => t(k('h1'), i18nVars.value),
    'url': () => (locale.value === 'en' ? canonicalUrl.value : pageUrl.value),
    'inLanguage': locale.value === 'en' ? 'en-GB' : 'fr-FR',
  }),
  {
    '@type': 'BreadcrumbList',
    'itemListElement': () => {
      const siteUrl = config.public.siteUrl as string
      const items: Array<{ '@type': 'ListItem', 'position': number, 'name': string, 'item'?: string }> = [
        { '@type': 'ListItem', 'position': 1, 'name': 'Holicosy', 'item': siteUrl },
      ]
      let pos = 2
      if (department.value) {
        items.push({ '@type': 'ListItem', 'position': pos++, 'name': department.value.region.name, 'item': `${siteUrl}${localePath(`/regions/${department.value.region.slug}`)}` })
        items.push({ '@type': 'ListItem', 'position': pos++, 'name': `${department.value.code} ${department.value.name}`, 'item': `${siteUrl}${localePath(`/${codeSlug.value}`)}` })
      }
      items.push({ '@type': 'ListItem', 'position': pos, 'name': cityName.value, 'item': pageUrl.value })
      return items
    },
  },
  ...(faqItems.value.length > 0
    ? [{
        '@type': 'FAQPage' as const,
        'mainEntity': faqItems.value.map(item => ({
          '@type': 'Question' as const,
          'name': item.question,
          'acceptedAnswer': { '@type': 'Answer' as const, 'text': item.answer },
        })),
      }]
    : []),
])

const hoveredEstablishmentId = ref<string | null>(null)
</script>

<template>
  <div class="min-h-screen bg-paper-50 pb-24 md:pb-0">
    <template v-if="data">
      <div class="container mx-auto px-4 py-6">
        <UBreadcrumb
          :items="breadcrumbItems"
          separator-icon="i-lucide-chevron-right"
          class="mb-4"
        />

        <header class="mb-8">
          <span class="tag text-paper-900/60">{{ t('pages.ville.eyebrow', i18nVars) }}</span>
          <h1 class="font-display font-light text-display-xs md:text-display-sm text-paper-900 tracking-[-0.02em] leading-[0.98] mt-3">
            {{ t(k('h1'), i18nVars) }}
          </h1>
          <p class="text-[15px] text-paper-900/60 mt-3">
            {{ t(k('intro'), i18nVars, exactCount) }}
          </p>
        </header>

        <!-- Listing principal : ville exacte -->
        <section aria-labelledby="ville-list-heading">
          <h2 id="ville-list-heading" class="sr-only">
            {{ t('pages.ville.listTitle', i18nVars) }}
          </h2>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr">
            <div
              v-for="establishment in data.exact"
              :key="establishment.id"
              :data-establishment-card="establishment.id"
              class="h-full"
            >
              <HoliPropertyCardV3
                :establishment="establishment"
                :highlighted-id="hoveredEstablishmentId"
                @hover="hoveredEstablishmentId = $event"
              />
            </div>
          </div>
        </section>

        <!-- Section proximité : rayon, ville différente. Bloc séparé, jamais
             fondu dans le listing principal (anti-cannibalisation). -->
        <section
          v-if="data.nearby.length > 0"
          class="mt-16"
          aria-labelledby="ville-nearby-heading"
        >
          <h2
            id="ville-nearby-heading"
            class="font-display font-light text-display-xs text-paper-900 mb-6"
          >
            {{ t('pages.ville.nearbyTitle', i18nVars) }}
          </h2>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr">
            <div
              v-for="establishment in data.nearby"
              :key="establishment.id"
              :data-establishment-card="establishment.id"
              class="flex flex-col gap-2 h-full"
            >
              <HoliPropertyCardV3
                :establishment="establishment"
                :highlighted-id="hoveredEstablishmentId"
                @hover="hoveredEstablishmentId = $event"
              />
              <p class="text-sm text-paper-900/60 pl-1">
                {{ t('pages.poi.distanceLabel', { distance: establishment.distanceKm.toFixed(1), poi: cityName }) }}
              </p>
            </div>
          </div>
        </section>

        <!-- FAQ templatée -->
        <section class="mt-16" aria-labelledby="ville-faq-heading">
          <h2 id="ville-faq-heading" class="font-display font-light text-display-xs text-paper-900 mb-6">
            {{ t(k('faq.title'), i18nVars) }}
          </h2>
          <div class="space-y-4">
            <details
              v-for="item in faqItems"
              :key="item.question"
              class="bg-paper-50 rounded-2xl border hairline p-4 group"
            >
              <summary class="font-display font-normal text-paper-900 cursor-pointer list-none flex items-center justify-between">
                <span>{{ item.question }}</span>
                <UIcon name="i-lucide-chevron-down" class="w-5 h-5 transition-transform group-open:rotate-180" />
              </summary>
              <p class="mt-3 text-paper-900/80 leading-[1.55]">
                {{ item.answer }}
              </p>
            </details>
          </div>
        </section>
      </div>
    </template>
  </div>
</template>
